import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { StageBadge } from "./StageBadge";
import { api, formatDate, formatDateTime, initials } from "../lib/api";
import { toast } from "sonner";
import { Phone, Envelope, MapPin, LinkedinLogo, IdentificationCard, Buildings, CheckCircle, Trash, PencilSimple, Plus, Calendar, EnvelopeSimple } from "@phosphor-icons/react";

import { ComposeEmailDialog } from "./ComposeEmailDialog";
import { STAGES } from "../lib/constants";

const STAGES_LOCAL_DEPRECATED = [];

export const PhysicianDetailSheet = ({ physicianId, open, onOpenChange, onUpdated }) => {
  const [physician, setPhysician] = useState(null);
  const [notes, setNotes] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [composeFu, setComposeFu] = useState(null);
  const [newFollowup, setNewFollowup] = useState({ label: "", scheduled_date: "", type: "call" });

  const refresh = async () => {
    if (!physicianId) return;
    const [p, n, f] = await Promise.all([
      api.get(`/physicians/${physicianId}`),
      api.get(`/physicians/${physicianId}/notes`),
      api.get(`/physicians/${physicianId}/followups`),
    ]);
    setPhysician(p.data);
    setNotes(n.data);
    setFollowups(f.data);
    setEditForm(p.data);
  };

  useEffect(() => { if (open && physicianId) refresh(); }, [open, physicianId]);

  if (!physician) return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl rounded-none">
        <SheetHeader>
          <SheetTitle className="sr-only">Loading physician details</SheetTitle>
          <SheetDescription className="sr-only">Please wait while data loads.</SheetDescription>
        </SheetHeader>
        <div className="p-8 font-mono text-xs uppercase tracking-wider text-[#5C5E66]">LOADING...</div>
      </SheetContent>
    </Sheet>
  );

  const updateStage = async (stage) => {
    await api.put(`/physicians/${physicianId}/stage`, { stage });
    toast.success(`Moved to ${stage}`);
    refresh();
    onUpdated?.();
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await api.post(`/physicians/${physicianId}/notes`, { content: newNote });
    setNewNote("");
    refresh();
  };

  const deleteNote = async (id) => {
    await api.delete(`/notes/${id}`);
    refresh();
  };

  const toggleFollowup = async (fu) => {
    await api.put(`/followups/${fu.id}`, { completed: !fu.completed });
    refresh();
  };

  const updateFollowupDate = async (id, date) => {
    await api.put(`/followups/${id}`, { scheduled_date: new Date(date).toISOString() });
    refresh();
  };

  const deleteFollowup = async (id) => {
    await api.delete(`/followups/${id}`);
    refresh();
  };

  const addFollowup = async () => {
    if (!newFollowup.label || !newFollowup.scheduled_date) { toast.error("Label & date required"); return; }
    await api.post(`/physicians/${physicianId}/followups`, {
      ...newFollowup,
      scheduled_date: new Date(newFollowup.scheduled_date).toISOString()
    });
    setNewFollowup({ label: "", scheduled_date: "", type: "call" });
    refresh();
  };

  const saveEdit = async () => {
    await api.put(`/physicians/${physicianId}`, editForm);
    toast.success("Updated");
    setEditing(false);
    refresh();
    onUpdated?.();
  };

  const removePhysician = async () => {
    if (!window.confirm("Delete this physician permanently?")) return;
    await api.delete(`/physicians/${physicianId}`);
    toast.success("Deleted");
    onOpenChange(false);
    onUpdated?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl rounded-none border-[#E2E2E5] p-0 overflow-y-auto" data-testid="physician-detail-sheet">
        <SheetHeader className="border-b border-[#E2E2E5] p-6 sticky top-0 bg-white z-10">
          <SheetDescription className="sr-only">Physician profile details, notes and follow-up cadence.</SheetDescription>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-[#0A0B0F] text-white flex items-center justify-center font-display font-bold text-xl">
              {initials(physician.full_name)}
            </div>
            <div className="flex-1">
              <SheetTitle className="font-display font-black tracking-tight text-2xl text-left" data-testid="detail-name">
                {physician.full_name}
              </SheetTitle>
              <div className="text-sm text-[#5C5E66] mt-0.5">{physician.specialty || "—"} · {physician.practice_type}</div>
              <div className="flex items-center gap-2 mt-2">
                <StageBadge stage={physician.stage} />
                <Select value={physician.stage} onValueChange={updateStage}>
                  <SelectTrigger data-testid="detail-stage-select" className="rounded-none h-7 text-xs w-44 font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Button
                data-testid="header-compose-btn"
                variant="outline"
                size="sm"
                className="rounded-none border-[#006CFF] text-[#006CFF] hover:bg-[#006CFF14]"
                onClick={() => setComposeFu({ autoStageMatch: "" })}
                title="Compose email"
              >
                <EnvelopeSimple size={14} />
              </Button>
              <Button data-testid="edit-toggle-btn" variant="outline" size="sm" className="rounded-none" onClick={() => setEditing(!editing)}>
                <PencilSimple size={14} />
              </Button>
              <Button variant="outline" size="sm" className="rounded-none border-[#C62828] text-[#C62828] hover:bg-[#C6282810]" onClick={removePhysician} data-testid="delete-physician-btn">
                <Trash size={14} />
              </Button>
            </div>
          </div>
        </SheetHeader>

        <Tabs defaultValue="profile" className="p-6">
          <TabsList className="rounded-none bg-[#F0F0F2] border border-[#E2E2E5] p-0 h-auto">
            <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-none data-[state=active]:bg-[#0A0B0F] data-[state=active]:text-white px-4 py-2 font-mono text-xs uppercase tracking-wider">Profile</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes" className="rounded-none data-[state=active]:bg-[#0A0B0F] data-[state=active]:text-white px-4 py-2 font-mono text-xs uppercase tracking-wider">Notes ({notes.length})</TabsTrigger>
            <TabsTrigger value="cadence" data-testid="tab-cadence" className="rounded-none data-[state=active]:bg-[#0A0B0F] data-[state=active]:text-white px-4 py-2 font-mono text-xs uppercase tracking-wider">Follow-ups ({followups.filter(f=>!f.completed).length})</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6 space-y-4">
            {!editing ? (
              <>
                <InfoRow icon={IdentificationCard} label="NPI" value={physician.npi || "—"} mono />
                <InfoRow icon={Buildings} label="Practice" value={physician.practice_name || "—"} />
                <InfoRow icon={Envelope} label="Email" value={physician.email || "—"} />
                <InfoRow icon={Phone} label="Phone" value={physician.phone || "—"} mono />
                <InfoRow icon={MapPin} label="Location" value={[physician.address, physician.city, physician.state, physician.zipcode].filter(Boolean).join(", ") || "—"} />
                <InfoRow icon={LinkedinLogo} label="LinkedIn" value={physician.linkedin_url ? <a href={physician.linkedin_url} target="_blank" rel="noreferrer" className="text-[#006CFF] underline">View Profile</a> : "—"} />

                <div className="mt-6 border-t border-[#E2E2E5] pt-4">
                  <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-3">BD Pipeline</div>
                  <InfoRow icon={Calendar} label="Territory" value={physician.territory || "—"} />
                  <InfoRow icon={Calendar} label="BD Rep" value={physician.bd_rep || "—"} />
                  <InfoRow icon={Calendar} label="Priority" value={
                    physician.priority ? (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider" style={{ background: physician.priority === "High" ? "#C6282814" : physician.priority === "Low" ? "#5C5E6614" : "#FF990014", color: physician.priority === "High" ? "#C62828" : physician.priority === "Low" ? "#5C5E66" : "#C97000" }}>
                        {physician.priority}
                      </span>
                    ) : "—"
                  } />
                  <InfoRow icon={Calendar} label="Patient Volume" value={physician.patient_volume_est || "—"} mono />
                  <InfoRow icon={Calendar} label="EMR System" value={physician.emr_system || "—"} />
                  <InfoRow icon={Calendar} label="Next Action" value={physician.next_action ? `${physician.next_action}${physician.next_action_date ? " (" + new Date(physician.next_action_date).toLocaleDateString() + ")" : ""}` : "—"} />
                </div>
                {physician.ai_summary && (
                  <div className="mt-6 border-t border-[#E2E2E5] pt-4">
                    <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-2">AI Summary</div>
                    <p className="text-sm leading-relaxed">{physician.ai_summary}</p>
                  </div>
                )}
                {physician.research_experience && (
                  <div className="mt-4">
                    <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-2">Research Experience</div>
                    <p className="text-sm leading-relaxed">{physician.research_experience}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {["full_name","email","phone","practice_name","npi","linkedin_url","city","state","zipcode"].map(k => (
                  <div key={k} className={k === "full_name" || k === "linkedin_url" ? "col-span-2" : ""}>
                    <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">{k.replace(/_/g," ")}</Label>
                    <Input data-testid={`edit-${k}`} value={editForm[k] || ""} onChange={(e) => setEditForm({...editForm, [k]: e.target.value})} className="rounded-none mt-1" />
                  </div>
                ))}
                <div className="col-span-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Research Experience</Label>
                  <Textarea value={editForm.research_experience || ""} onChange={(e) => setEditForm({...editForm, research_experience: e.target.value})} className="rounded-none mt-1" rows={3} />
                </div>
                <div className="col-span-2 flex gap-2">
                  <Button onClick={saveEdit} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none" data-testid="save-edit-btn">Save</Button>
                  <Button variant="outline" className="rounded-none" onClick={() => { setEditing(false); setEditForm(physician); }}>Cancel</Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="notes" className="mt-6 space-y-3">
            <div className="flex gap-2">
              <Textarea data-testid="new-note-input" placeholder="Add a note about this physician..." value={newNote} onChange={(e) => setNewNote(e.target.value)} className="rounded-none flex-1" rows={2} />
              <Button data-testid="add-note-btn" onClick={addNote} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none self-stretch">Add</Button>
            </div>
            <div className="border-t border-[#E2E2E5] pt-3 space-y-2">
              {notes.length === 0 && <div className="text-center py-8 text-[#5C5E66] font-mono text-xs uppercase tracking-wider">No notes yet</div>}
              {notes.map((n) => (
                <div key={n.id} className="border border-[#E2E2E5] p-3 bg-white" data-testid={`note-${n.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 text-sm whitespace-pre-wrap">{n.content}</div>
                    <button onClick={() => deleteNote(n.id)} className="text-[#5C5E66] hover:text-[#C62828]"><Trash size={14} /></button>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#5C5E66] mt-2">{formatDateTime(n.created_at)}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="cadence" className="mt-6 space-y-3">
            <div className="border border-[#E2E2E5] p-4 bg-white">
              <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-3">Add Follow-up</div>
              <div className="grid grid-cols-3 gap-2">
                <Input data-testid="new-fu-label" placeholder="Label" value={newFollowup.label} onChange={(e) => setNewFollowup({...newFollowup, label: e.target.value})} className="rounded-none" />
                <Input data-testid="new-fu-date" type="datetime-local" value={newFollowup.scheduled_date} onChange={(e) => setNewFollowup({...newFollowup, scheduled_date: e.target.value})} className="rounded-none" />
                <Select value={newFollowup.type} onValueChange={(v) => setNewFollowup({...newFollowup, type: v})}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="add-fu-btn" onClick={addFollowup} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none mt-2 gap-1.5"><Plus size={14} weight="bold" /> Schedule</Button>
            </div>

            {followups.length === 0 && <div className="text-center py-8 text-[#5C5E66] font-mono text-xs uppercase tracking-wider">No follow-ups</div>}
            {followups.map((fu) => {
              const overdue = !fu.completed && new Date(fu.scheduled_date) < new Date();
              return (
                <div key={fu.id} className={`border p-3 bg-white flex items-center gap-3 ${overdue ? "border-[#E65100]" : "border-[#E2E2E5]"}`} data-testid={`followup-${fu.id}`}>
                  <button onClick={() => toggleFollowup(fu)} data-testid={`fu-toggle-${fu.id}`} className={`w-5 h-5 border ${fu.completed ? "bg-[#2E7D32] border-[#2E7D32]" : "border-[#5C5E66]"} flex items-center justify-center flex-shrink-0`}>
                    {fu.completed && <CheckCircle size={14} weight="fill" className="text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${fu.completed ? "line-through text-[#5C5E66]" : ""}`}>{fu.label}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#F0F0F2]">{fu.type}</span>
                      {overdue && <span className="text-[10px] font-mono uppercase tracking-wider text-[#E65100]">OVERDUE</span>}
                    </div>
                  </div>
                  <Input
                    type="datetime-local"
                    value={fu.scheduled_date ? new Date(fu.scheduled_date).toISOString().slice(0,16) : ""}
                    onChange={(e) => updateFollowupDate(fu.id, e.target.value)}
                    className="rounded-none w-44 text-xs font-mono"
                    data-testid={`fu-date-${fu.id}`}
                  />
                  <button
                    onClick={() => setComposeFu({ autoStageMatch: fu.label })}
                    data-testid={`fu-compose-${fu.id}`}
                    title="Compose email for this follow-up"
                    className="p-1 hover:bg-[#006CFF14] text-[#006CFF]"
                  >
                    <EnvelopeSimple size={14} weight="regular" />
                  </button>
                  <button onClick={() => deleteFollowup(fu.id)} className="text-[#5C5E66] hover:text-[#C62828]"><Trash size={14} /></button>
                </div>
              );
            })}
          </TabsContent>
        </Tabs>

        <ComposeEmailDialog
          physicianId={composeFu ? physicianId : null}
          open={!!composeFu}
          onOpenChange={(o) => !o && setComposeFu(null)}
          autoStageMatch={composeFu?.autoStageMatch}
        />
      </SheetContent>
    </Sheet>
  );
};

const InfoRow = ({ icon: Icon, label, value, mono }) => (
  <div className="grid grid-cols-[140px_1fr] gap-4 items-start py-2 border-b border-[#E2E2E5] last:border-0">
    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#5C5E66]">
      <Icon size={14} />{label}
    </div>
    <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
  </div>
);
